<script lang="ts">
  import { grammarActions } from '$lib/stores/grammar'
  import { textLayersActions } from '$lib/stores/textLayers'
  import type { Snippet } from 'svelte'
  import { useError } from '$lib/hooks/useError'
  import { errorService } from '$lib/services/ErrorService'
  
  interface Props {
    value: string
    onTextChange?: (text: string) => void
    children?: Snippet<[string]>
  }
  
  let { value = $bindable(), onTextChange, children }: Props = $props()
  
  // Error handling setup
  const { tryExecute } = useError('TextManager')
  
  /**
   * TextManager: Handles text state synchronization
   * Single Responsibility: Managing text flow between stores
   */
  
  // Track previous value to prevent unnecessary updates
  let previousValue = ''
  
  // Sync text changes to stores (only when value actually changes)
  $effect(() => {
    if (value !== previousValue) {
      previousValue = value
      grammarActions.setText(value)
      textLayersActions.setCurrent(value)
      onTextChange?.(value)
    }
  })
  
  export function clearText() {
    value = ''
    grammarActions.reset()
    textLayersActions.reset()
  }
  
  export function setText(text: string) {
    value = text
  }
  
  export function getSelection(): { text: string; start: number; end: number } | null {
    // This will be passed through from the editor component
    return null
  }
  
  export async function pasteText() {
    const [text, error] = await tryExecute(
      () => navigator.clipboard.readText(),
      {
        severity: 'warning',
        context: { action: 'pasteText' },
        showToUser: false
      }
    )
    
    if (text) {
      value = text
      errorService.showSuccess('텍스트를 붙여넣었습니다.', 2000)
    } else if (error) {
      // Handle clipboard permission errors specifically
      if (error.message?.includes('permission') || error.message?.includes('denied')) {
        errorService.showWarning('클립보드 접근 권한이 필요합니다.');
      } else {
        errorService.showWarning('붙여넣기에 실패했습니다. 다시 시도해주세요.');
      }
    }
  }
  
  export async function copyText() {
    const [result, error] = await tryExecute(
      () => navigator.clipboard.writeText(value),
      {
        severity: 'warning',
        context: { action: 'copyText' },
        showToUser: false
      }
    )
    
    if (result !== null) {
      errorService.showSuccess('텍스트를 복사했습니다.', 2000)
    } else if (error) {
      // Handle clipboard permission errors specifically
      if (error.message?.includes('permission') || error.message?.includes('denied')) {
        errorService.showWarning('클립보드 접근 권한이 필요합니다.');
      } else {
        errorService.showWarning('복사에 실패했습니다. 다시 시도해주세요.');
      }
    }
  }
</script>

<!-- This component has no UI - it only manages text state -->
{#if children}
  {@render children(value)}
{/if}